import React, { useState, useEffect, useCallback } from 'react';
import { Shield, ChevronDown, ChevronRight, Check, Users, Loader2, History, Plus, Edit2, X, Lock } from 'lucide-react';
import { adminApi } from '../../services/api';

export default function RoleManagement() {
  const [roles, setRoles] = useState([]);
  const [permissions, setPermissions] = useState([]);
  const [auditLog, setAuditLog] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedRole, setExpandedRole] = useState(null);
  const [editingPerms, setEditingPerms] = useState(null);
  const [showCreateRole, setShowCreateRole] = useState(false);
  const [editingRole, setEditingRole] = useState(null);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState('roles');

  const loadData = useCallback(async () => {
    try {
      const [rolesRes, permsRes, auditRes] = await Promise.all([
        adminApi.listRoles(),
        adminApi.listPermissions(),
        adminApi.getAuditLog(100),
      ]);
      setRoles(rolesRes.data);
      setPermissions(permsRes.data);
      setAuditLog(auditRes.data);
    } catch (err) {
      console.error('Failed to load roles data:', err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadData(); }, [loadData]);

  const permsByCategory = permissions.reduce((acc, p) => {
    if (!acc[p.category]) acc[p.category] = [];
    acc[p.category].push(p);
    return acc;
  }, {});

  const isSysadmin = (role) => role.role_key === 'sysadmin';

  const handleEditPerms = (role) => {
    if (isSysadmin(role)) return;
    setEditingPerms({
      roleId: role.role_id,
      roleName: role.role_name,
      permissionIds: role.permissions.map((p) => p.permission_id),
    });
  };

  const togglePerm = (permId) => {
    setEditingPerms((prev) => ({
      ...prev,
      permissionIds: prev.permissionIds.includes(permId)
        ? prev.permissionIds.filter((id) => id !== permId)
        : [...prev.permissionIds, permId],
    }));
  };

  const toggleCategory = (category) => {
    const catPermIds = permsByCategory[category].map((p) => p.permission_id);
    const allSelected = catPermIds.every((id) => editingPerms.permissionIds.includes(id));
    setEditingPerms((prev) => ({
      ...prev,
      permissionIds: allSelected
        ? prev.permissionIds.filter((id) => !catPermIds.includes(id))
        : [...new Set([...prev.permissionIds, ...catPermIds])],
    }));
  };

  const handleSavePerms = async () => {
    setSaving(true);
    try {
      await adminApi.updateRolePermissions(editingPerms.roleId, editingPerms.permissionIds);
      setEditingPerms(null);
      await loadData();
    } catch (err) {
      alert(err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleCreateRole = async (data) => {
    setSaving(true);
    try {
      await adminApi.createRole(data);
      setShowCreateRole(false);
      await loadData();
    } catch (err) {
      alert(err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleUpdateRole = async (roleId, data) => {
    setSaving(true);
    try {
      await adminApi.updateRole(roleId, data);
      setEditingRole(null);
      await loadData();
    } catch (err) {
      alert(err.message);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="w-8 h-8 text-amber-500 animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <Shield className="w-7 h-7 text-amber-500" />
            Role & Permission Management
          </h1>
          <p className="text-sm text-gray-500 mt-1">Manage roles and their permission assignments</p>
        </div>
        <button
          onClick={() => setShowCreateRole(true)}
          className="inline-flex items-center gap-2 px-4 py-2 bg-amber-400 text-gray-900 rounded-lg hover:bg-amber-500 font-medium text-sm"
        >
          <Plus className="w-4 h-4" /> Create Role
        </button>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 border-b border-gray-200">
        <button
          onClick={() => setActiveTab('roles')}
          className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
            activeTab === 'roles'
              ? 'border-amber-500 text-amber-700'
              : 'border-transparent text-gray-500 hover:text-gray-700'
          }`}
        >
          <span className="flex items-center gap-1.5"><Shield className="w-4 h-4" /> Roles ({roles.length})</span>
        </button>
        <button
          onClick={() => setActiveTab('audit')}
          className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
            activeTab === 'audit'
              ? 'border-amber-500 text-amber-700'
              : 'border-transparent text-gray-500 hover:text-gray-700'
          }`}
        >
          <span className="flex items-center gap-1.5"><History className="w-4 h-4" /> Audit Log</span>
        </button>
      </div>

      {activeTab === 'roles' && (
        <div className="grid gap-4">
          {roles.map((role) => {
            const isExpanded = expandedRole === role.role_id;
            const locked = isSysadmin(role);
            return (
              <div key={role.role_id} className="bg-white rounded-xl border border-gray-200 overflow-hidden">
                <div
                  className="flex items-center justify-between px-5 py-4 cursor-pointer hover:bg-gray-50"
                  onClick={() => setExpandedRole(isExpanded ? null : role.role_id)}
                >
                  <div className="flex items-center gap-3">
                    <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                      role.role_key === 'sysadmin' ? 'bg-red-100' :
                      role.role_key === 'admin' ? 'bg-purple-100' :
                      role.role_key === 'analyst' ? 'bg-blue-100' : 'bg-gray-100'
                    }`}>
                      {locked ? (
                        <Lock className="w-5 h-5 text-red-600" />
                      ) : (
                        <Shield className={`w-5 h-5 ${
                          role.role_key === 'admin' ? 'text-purple-600' :
                          role.role_key === 'analyst' ? 'text-blue-600' : 'text-gray-500'
                        }`} />
                      )}
                    </div>
                    <div>
                      <div className="font-semibold text-gray-900 flex items-center gap-2">
                        {role.role_name}
                        {locked && (
                          <span className="text-[10px] bg-red-50 text-red-600 px-1.5 py-0.5 rounded font-medium">LOCKED</span>
                        )}
                        {!role.is_active && (
                          <span className="text-[10px] bg-gray-100 text-gray-500 px-1.5 py-0.5 rounded font-medium">INACTIVE</span>
                        )}
                      </div>
                      <div className="text-xs text-gray-400">{role.description}</div>
                    </div>
                  </div>
                  <div className="flex items-center gap-4">
                    <div className="text-right">
                      <div className="text-sm font-medium text-gray-700">{role.permissions.length} permissions</div>
                      <div className="text-xs text-gray-400 flex items-center gap-1">
                        <Users className="w-3 h-3" /> {role.userCount} users
                      </div>
                    </div>
                    {isExpanded ? (
                      <ChevronDown className="w-5 h-5 text-gray-400" />
                    ) : (
                      <ChevronRight className="w-5 h-5 text-gray-400" />
                    )}
                  </div>
                </div>

                {isExpanded && (
                  <div className="border-t border-gray-200 px-5 py-4">
                    <div className="flex items-center justify-between mb-3">
                      <h4 className="text-sm font-semibold text-gray-700">Assigned Permissions</h4>
                      <div className="flex gap-2">
                        {!locked && (
                          <>
                            <button
                              onClick={(e) => { e.stopPropagation(); setEditingRole(role); }}
                              className="text-xs px-3 py-1.5 bg-blue-50 text-blue-700 rounded-lg hover:bg-blue-100 font-medium flex items-center gap-1"
                            >
                              <Edit2 className="w-3 h-3" /> Edit Role
                            </button>
                            <button
                              onClick={(e) => { e.stopPropagation(); handleEditPerms(role); }}
                              className="text-xs px-3 py-1.5 bg-amber-50 text-amber-700 rounded-lg hover:bg-amber-100 font-medium"
                            >
                              Edit Permissions
                            </button>
                          </>
                        )}
                        {locked && (
                          <span className="text-xs text-gray-400 italic flex items-center gap-1">
                            <Lock className="w-3 h-3" /> System role — cannot be modified
                          </span>
                        )}
                      </div>
                    </div>
                    {Object.entries(permsByCategory).map(([category, catPerms]) => {
                      const assigned = catPerms.filter((p) =>
                        role.permissions.some((rp) => rp.permission_id === p.permission_id)
                      );
                      if (assigned.length === 0) return null;
                      return (
                        <div key={category} className="mb-2">
                          <div className="text-xs font-medium text-gray-400 uppercase tracking-wide mb-1">
                            {category}
                          </div>
                          <div className="flex flex-wrap gap-1.5">
                            {assigned.map((p) => (
                              <span key={p.permission_id} className="inline-flex items-center gap-1 px-2 py-0.5 bg-green-50 text-green-700 rounded text-xs">
                                <Check className="w-3 h-3" /> {p.permission_name}
                              </span>
                            ))}
                          </div>
                        </div>
                      );
                    })}
                    {role.permissions.length === 0 && (
                      <p className="text-sm text-gray-400 italic">No permissions assigned</p>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {activeTab === 'audit' && (
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-gray-50 border-b border-gray-200">
                  <th className="text-left px-4 py-3 font-semibold text-gray-700">Action</th>
                  <th className="text-left px-4 py-3 font-semibold text-gray-700">Target</th>
                  <th className="text-left px-4 py-3 font-semibold text-gray-700">Performed By</th>
                  <th className="text-left px-4 py-3 font-semibold text-gray-700">Details</th>
                  <th className="text-left px-4 py-3 font-semibold text-gray-700">Date</th>
                </tr>
              </thead>
              <tbody>
                {auditLog.map((entry) => (
                  <tr key={entry.log_id} className="border-b border-gray-100">
                    <td className="px-4 py-3">
                      <span className={`inline-flex px-2 py-0.5 rounded text-xs font-medium ${
                        entry.action.includes('assigned') || entry.action.includes('created') ? 'bg-green-50 text-green-700' :
                        entry.action.includes('revoked') || entry.action.includes('deleted') || entry.action.includes('deactivated')
                          ? 'bg-red-50 text-red-700'
                          : 'bg-blue-50 text-blue-700'
                      }`}>
                        {entry.action.replace(/_/g, ' ')}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-gray-600">{entry.target_email || `User #${entry.target_user_id || '—'}`}</td>
                    <td className="px-4 py-3 text-gray-500">{entry.performed_by}</td>
                    <td className="px-4 py-3 text-gray-400 text-xs max-w-xs truncate">{entry.details}</td>
                    <td className="px-4 py-3 text-gray-500 text-xs">
                      {new Date(entry.created_at).toLocaleString('en-US', {
                        month: 'short', day: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit',
                      })}
                    </td>
                  </tr>
                ))}
                {auditLog.length === 0 && (
                  <tr>
                    <td colSpan={5} className="px-4 py-8 text-center text-gray-400">No audit entries</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Create Role Modal */}
      {showCreateRole && (
        <CreateRoleModal
          permissions={permissions}
          permsByCategory={permsByCategory}
          saving={saving}
          onSave={handleCreateRole}
          onClose={() => setShowCreateRole(false)}
        />
      )}

      {/* Edit Role Modal */}
      {editingRole && (
        <EditRoleModal
          role={editingRole}
          saving={saving}
          onSave={(data) => handleUpdateRole(editingRole.role_id, data)}
          onClose={() => setEditingRole(null)}
        />
      )}

      {/* Edit Permissions Modal */}
      {editingPerms && (
        <PermissionsModal
          editingPerms={editingPerms}
          permsByCategory={permsByCategory}
          saving={saving}
          onTogglePerm={togglePerm}
          onToggleCategory={toggleCategory}
          onSave={handleSavePerms}
          onClose={() => setEditingPerms(null)}
        />
      )}
    </div>
  );
}

// ─── Modals ─────────────────────────────────────────────

function ModalWrapper({ title, onClose, children, wide }) {
  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <div className={`bg-white rounded-xl shadow-xl w-full ${wide ? 'max-w-lg' : 'max-w-md'} max-h-[80vh] flex flex-col`}>
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 flex-shrink-0">
          <h3 className="text-lg font-semibold text-gray-900">{title}</h3>
          <button onClick={onClose} className="p-1 rounded hover:bg-gray-100">
            <X className="w-5 h-5 text-gray-400" />
          </button>
        </div>
        {children}
      </div>
    </div>
  );
}

function CreateRoleModal({ permissions, permsByCategory, saving, onSave, onClose }) {
  const [roleKey, setRoleKey] = useState('');
  const [roleName, setRoleName] = useState('');
  const [description, setDescription] = useState('');
  const [selectedPerms, setSelectedPerms] = useState([]);

  const togglePerm = (id) => {
    setSelectedPerms((prev) => prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]);
  };

  const toggleCategory = (category) => {
    const ids = permsByCategory[category].map((p) => p.permission_id);
    const allIn = ids.every((id) => selectedPerms.includes(id));
    setSelectedPerms((prev) =>
      allIn ? prev.filter((id) => !ids.includes(id)) : [...new Set([...prev, ...ids])]
    );
  };

  // Auto-generate role_key from name
  const handleNameChange = (name) => {
    setRoleName(name);
    if (!roleKey || roleKey === slugify(roleName)) {
      setRoleKey(slugify(name));
    }
  };

  return (
    <ModalWrapper title="Create New Role" onClose={onClose} wide>
      <div className="overflow-y-auto px-6 py-4 flex-1">
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Role Name *</label>
            <input
              type="text"
              value={roleName}
              onChange={(e) => handleNameChange(e.target.value)}
              className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-amber-400"
              placeholder="e.g. Report Manager"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Role Key *</label>
            <input
              type="text"
              value={roleKey}
              onChange={(e) => setRoleKey(e.target.value.toLowerCase().replace(/[^a-z0-9_-]/g, ''))}
              className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm font-mono focus:outline-none focus:ring-2 focus:ring-amber-400"
              placeholder="e.g. report_manager"
            />
            <p className="text-xs text-gray-400 mt-1">Lowercase, underscores allowed. Used as unique identifier.</p>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
            <input
              type="text"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-amber-400"
              placeholder="What can this role do?"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Permissions ({selectedPerms.length} selected)
            </label>
            {Object.entries(permsByCategory).map(([category, catPerms]) => {
              const ids = catPerms.map((p) => p.permission_id);
              const allIn = ids.every((id) => selectedPerms.includes(id));
              const someIn = ids.some((id) => selectedPerms.includes(id));
              return (
                <div key={category} className="mb-3">
                  <label className="flex items-center gap-2 mb-1 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={allIn}
                      ref={(el) => { if (el) el.indeterminate = someIn && !allIn; }}
                      onChange={() => toggleCategory(category)}
                      className="rounded border-gray-300 text-amber-500 focus:ring-amber-400"
                    />
                    <span className="text-xs font-semibold text-gray-600 uppercase tracking-wide">{category}</span>
                  </label>
                  <div className="ml-6 space-y-1">
                    {catPerms.map((p) => (
                      <label key={p.permission_id} className="flex items-center gap-2 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={selectedPerms.includes(p.permission_id)}
                          onChange={() => togglePerm(p.permission_id)}
                          className="rounded border-gray-300 text-amber-500 focus:ring-amber-400"
                        />
                        <span className="text-sm text-gray-700">{p.permission_name}</span>
                      </label>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
      <div className="flex justify-end gap-2 px-6 py-4 border-t border-gray-200 flex-shrink-0">
        <button onClick={onClose} className="px-4 py-2 text-sm text-gray-600 hover:bg-gray-100 rounded-lg">Cancel</button>
        <button
          onClick={() => onSave({ roleKey, roleName, description, permissionIds: selectedPerms })}
          disabled={!roleKey || !roleName || saving}
          className="px-4 py-2 text-sm bg-amber-400 text-gray-900 rounded-lg hover:bg-amber-500 disabled:opacity-50 font-medium"
        >
          {saving ? 'Creating...' : 'Create Role'}
        </button>
      </div>
    </ModalWrapper>
  );
}

function EditRoleModal({ role, saving, onSave, onClose }) {
  const [roleName, setRoleName] = useState(role.role_name);
  const [description, setDescription] = useState(role.description || '');
  const [isActive, setIsActive] = useState(role.is_active);

  return (
    <ModalWrapper title="Edit Role" onClose={onClose}>
      <div className="px-6 py-4">
        <div className="space-y-4">
          <div className="text-sm text-gray-500 font-mono bg-gray-50 px-3 py-1.5 rounded">{role.role_key}</div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Role Name</label>
            <input
              type="text"
              value={roleName}
              onChange={(e) => setRoleName(e.target.value)}
              className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-amber-400"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
            <input
              type="text"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-amber-400"
            />
          </div>
          <div>
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={isActive}
                onChange={(e) => setIsActive(e.target.checked)}
                className="rounded border-gray-300 text-amber-500 focus:ring-amber-400"
              />
              <span className="text-sm text-gray-700">Active</span>
            </label>
            <p className="text-xs text-gray-400 mt-1 ml-6">Inactive roles won't grant any permissions</p>
          </div>
        </div>
      </div>
      <div className="flex justify-end gap-2 px-6 py-4 border-t border-gray-200">
        <button onClick={onClose} className="px-4 py-2 text-sm text-gray-600 hover:bg-gray-100 rounded-lg">Cancel</button>
        <button
          onClick={() => onSave({ roleName, description, isActive })}
          disabled={!roleName || saving}
          className="px-4 py-2 text-sm bg-amber-400 text-gray-900 rounded-lg hover:bg-amber-500 disabled:opacity-50 font-medium"
        >
          {saving ? 'Saving...' : 'Save Changes'}
        </button>
      </div>
    </ModalWrapper>
  );
}

function PermissionsModal({ editingPerms, permsByCategory, saving, onTogglePerm, onToggleCategory, onSave, onClose }) {
  return (
    <ModalWrapper title={`Edit Permissions — ${editingPerms.roleName}`} onClose={onClose} wide>
      <div className="overflow-y-auto px-6 py-4 flex-1">
        {Object.entries(permsByCategory).map(([category, catPerms]) => {
          const catPermIds = catPerms.map((p) => p.permission_id);
          const allSelected = catPermIds.every((id) => editingPerms.permissionIds.includes(id));
          const someSelected = catPermIds.some((id) => editingPerms.permissionIds.includes(id));

          return (
            <div key={category} className="mb-4">
              <label className="flex items-center gap-2 mb-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={allSelected}
                  ref={(el) => { if (el) el.indeterminate = someSelected && !allSelected; }}
                  onChange={() => onToggleCategory(category)}
                  className="rounded border-gray-300 text-amber-500 focus:ring-amber-400"
                />
                <span className="text-sm font-semibold text-gray-700 uppercase tracking-wide">{category}</span>
              </label>
              <div className="ml-6 space-y-1.5">
                {catPerms.map((p) => (
                  <label key={p.permission_id} className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={editingPerms.permissionIds.includes(p.permission_id)}
                      onChange={() => onTogglePerm(p.permission_id)}
                      className="rounded border-gray-300 text-amber-500 focus:ring-amber-400"
                    />
                    <span className="text-sm text-gray-700">{p.permission_name}</span>
                    <span className="text-xs text-gray-400">({p.permission_key})</span>
                  </label>
                ))}
              </div>
            </div>
          );
        })}
      </div>
      <div className="flex justify-between items-center px-6 py-4 border-t border-gray-200 flex-shrink-0">
        <span className="text-xs text-gray-400">{editingPerms.permissionIds.length} selected</span>
        <div className="flex gap-2">
          <button onClick={onClose} className="px-4 py-2 text-sm text-gray-600 hover:bg-gray-100 rounded-lg">Cancel</button>
          <button
            onClick={onSave}
            disabled={saving}
            className="px-4 py-2 text-sm bg-amber-400 text-gray-900 rounded-lg hover:bg-amber-500 disabled:opacity-50 font-medium"
          >
            {saving ? 'Saving...' : 'Save Permissions'}
          </button>
        </div>
      </div>
    </ModalWrapper>
  );
}

function slugify(str) {
  return (str || '').toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_|_$/g, '');
}
